import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useParams, Link } from "react-router-dom";
import BASE_URL from "../../../../config/config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiPlus, FiX, FiEdit2, FiTrash2, FiArrowLeft, FiList } from "react-icons/fi";
import Loader from "../../../../components/Loader";
import Popup from "../../../../components/Popup";

const TIPO_OPCIONES = [
  "Ratón",
  "Teclado",
  "Pantalla",
  "Impresora",
  "Altavoces",
  "Auriculares",
  "Dock",
  "Webcam",
  "Micrófono",
  "Lector de tarjetas",
  "Hub USB",
  "Cable",
  "Adaptador",
  "Alfombrilla",
  "SAI/UPS",
  "Tablet",
  "Móvil",
  "Otro",
];

const TIPO_ESTADO = ["Activo", "En reparación", "No activo"];

const claseEstado = (estado) => {
  if (estado === "Activo") return "mat-badge-estado-activo";
  if (estado === "En reparación") return "mat-badge-estado-reparacion";
  return "mat-badge-estado-inactivo";
};

const formVacio = (equipoIdParam) => ({
  marca: "",
  modelo: "",
  numero_serie: "",
  tipo: "",
  estado: "Activo",
  notas: "",
  equipoId: equipoIdParam || "",
});

const PerifericosManager = () => {
  const { id: equipoIdParam } = useParams();
  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};
  const isAdmin = tokenInfo?.role === "admin";

  const [perifericos, setPerifericos] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [equipoHeader, setEquipoHeader] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const [mostrarForm, setMostrarForm] = useState(false);
  const [form, setForm] = useState(formVacio(equipoIdParam));
  const [editId, setEditId] = useState(null);

  const [borrarId, setBorrarId] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: token || "",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const fetchPerifericos = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BASE_URL}/perifericos/`, { headers });
      setPerifericos(data || []);
    } catch (err) {
      console.error("Error al obtener periféricos:", err);
      toast.error("Error al obtener periféricos.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipos = async () => {
    try {
      const { data } = await axios.get(`${BASE_URL}/equipos/`, { headers });
      setEquipos(data || []);
    } catch (err) {
      console.error("Error al obtener equipos:", err);
      toast.error("Error al obtener equipos.");
    }
  };

  const fetchEquipoHeader = async (idEquipo) => {
    if (!idEquipo) {
      setEquipoHeader(null);
      return;
    }
    try {
      const { data } = await axios.get(`${BASE_URL}/equipos/${idEquipo}`, { headers });
      setEquipoHeader(data || null);
    } catch (err) {
      console.error("Error al obtener el equipo:", err);
      toast.error("No se pudo cargar la info del equipo.");
      setEquipoHeader(null);
    }
  };

  useEffect(() => {
    fetchEquipos();
    fetchPerifericos();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setForm((prev) => ({ ...prev, equipoId: equipoIdParam || "" }));
    if (equipoIdParam) fetchEquipoHeader(equipoIdParam);
    // eslint-disable-next-line
  }, [equipoIdParam]);

  const filtered = useMemo(() => {
    let list = perifericos;
    if (equipoIdParam) {
      list = list.filter((p) => String(p.equipoId) === String(equipoIdParam));
    }
    const q = searchText.toLowerCase();
    return list.filter((p) =>
      [
        p.marca,
        p.modelo,
        p.numero_serie,
        p.tipo,
        p.estado,
        p?.equipo?.modelo,
        p?.equipo?.marca,
        p?.equipo?.numero_serie,
        p.notas,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [perifericos, searchText, equipoIdParam]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const pageItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setForm(formVacio(equipoIdParam));
    setEditId(null);
  };

  const cerrarPanel = () => {
    setMostrarForm(false);
    resetForm();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      toast.error("Acceso restringido a administradores.");
      return;
    }

    try {
      if (editId) {
        await axios.put(`${BASE_URL}/perifericos/${editId}`, form, { headers });
        toast.success("Periférico actualizado correctamente.");
      } else {
        await axios.post(`${BASE_URL}/perifericos/`, form, { headers });
        toast.success("Periférico creado correctamente.");
      }
      await fetchPerifericos();
      cerrarPanel();
    } catch (err) {
      console.error("Error guardando periférico:", err);
      toast.error("Error guardando periférico.");
    }
  };

  const onEdit = (item) => {
    setEditId(item.id);
    setForm({
      marca: item.marca ?? "",
      modelo: item.modelo ?? "",
      numero_serie: item.numero_serie ?? "",
      tipo: item.tipo ?? "",
      estado: item.estado ?? "Activo",
      notas: item.notas ?? "",
      equipoId: (equipoIdParam || item.equipoId || "").toString(),
    });
    setMostrarForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pedirBorrado = (id) => {
    if (!isAdmin) {
      toast.error("Acceso restringido a administradores.");
      return;
    }
    setBorrarId(id);
  };

  const confirmarBorrado = async () => {
    const id = borrarId;
    setBorrarId(null);
    try {
      await axios.delete(`${BASE_URL}/perifericos/${id}`, { headers });
      toast.success("Periférico eliminado correctamente.");
      await fetchPerifericos();
    } catch (err) {
      console.error("Error al eliminar periférico:", err);
      toast.error("Error al eliminar periférico.");
    }
  };

  if (!token) {
    return (
      <div className="site-width">
        <p>Inicia sesión para continuar.</p>
        <ToastContainer position="bottom-right" />
      </div>
    );
  }

  return (
    <main className="site-width">
      <ToastContainer position="bottom-right" />
      <div className="dashboard-content gap-perifericos">
        <div className="pagina-cabecera">
          <h2>{equipoIdParam ? "Periféricos del equipo" : "Gestión de periféricos"}</h2>
          <p>Consulta y administra los periféricos asociados a cada equipo.</p>
        </div>

        {equipoIdParam && equipoHeader && (
          <div className="mat-equipo-banner">
            <div>
              <span className="mat-equipo-banner-label">Equipo #{equipoIdParam}</span>
              <span className="mat-equipo-banner-titulo">{equipoHeader.marca} {equipoHeader.modelo}</span>
            </div>
            <code className="mat-serie">{equipoHeader.numero_serie || "s/n"}</code>
          </div>
        )}
        {!equipoIdParam && (
          <p>Añade, edita y elimina periféricos, y asócialos a un equipo.</p>
        )}

        <div className="mat-toolbar-links">
          <Link to="/dashboard/equipos" className="mat-btn-secundario">
            <FiArrowLeft /> Volver a equipos
          </Link>
          {equipoIdParam && (
            <Link to="/dashboard/perifericos" className="mat-btn-secundario">
              <FiList /> Ver todos los periféricos
            </Link>
          )}
        </div>

        {/* Barra de herramientas */}
        <div className="mat-toolbar">
          <input
            type="text"
            placeholder={equipoIdParam ? "Buscar entre los periféricos de este equipo…" : "Buscar por nombre, marca, tipo, nº serie o equipo…"}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
          />
          <div className="mat-toolbar-acciones">
            <select
              className="selector-page-size"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={15}>15 / página</option>
              <option value={30}>30 / página</option>
              <option value={60}>60 / página</option>
            </select>
            <button
              type="button"
              className={`chip-toggle ${mostrarForm ? 'activo' : ''}`}
              onClick={() => (mostrarForm ? cerrarPanel() : setMostrarForm(true))}
            >
              <FiPlus /> Nuevo periférico
            </button>
          </div>
        </div>

        {/* Panel plegable: crear/editar periférico */}
        {mostrarForm && (
          <form className="mat-form-panel" onSubmit={onSubmit}>
            <div className="mat-form-panel-header">
              <h3>
                {editId
                  ? `Editar periférico #${editId}`
                  : equipoIdParam
                    ? `Nuevo periférico para el equipo #${equipoIdParam}`
                    : "Nuevo periférico"}
              </h3>
              <button type="button" className="mat-close-btn" onClick={cerrarPanel} aria-label="Cerrar">
                <FiX />
              </button>
            </div>

            <div className="mat-form-grid">
              <div className="mat-campo">
                <label>Marca</label>
                <input type="text" value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Modelo</label>
                <input type="text" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Nº Serie</label>
                <input type="text" value={form.numero_serie} onChange={(e) => setForm({ ...form, numero_serie: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Tipo</label>
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                  <option value="">— Selecciona tipo —</option>
                  {form.tipo && !TIPO_OPCIONES.includes(form.tipo) && (
                    <option value={form.tipo}>{form.tipo} (actual)</option>
                  )}
                  {TIPO_OPCIONES.map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>

              <div className="mat-campo">
                <label>Estado</label>
                <select value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} required>
                  {form.estado && !TIPO_ESTADO.includes(form.estado) && (
                    <option value={form.estado}>{form.estado} (actual)</option>
                  )}
                  {TIPO_ESTADO.map((estado) => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </select>
              </div>

              <div className="mat-campo">
                <label>Equipo asociado</label>
                <select value={form.equipoId} onChange={(e) => setForm({ ...form, equipoId: e.target.value })}>
                  <option value="">— Sin asignar —</option>
                  {equipos.map((eq) => (
                    <option key={eq.id} value={eq.id}>
                      #{eq.id} · {eq.marca} {eq.modelo} ({eq.numero_serie || "s/n"})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mat-campo mat-campo-full">
                <label>Notas</label>
                <textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
              </div>
            </div>

            <div className="mat-form-actions">
              <button type="button" className="mat-btn-secundario" onClick={resetForm}>Limpiar</button>
              <button type="submit" className="mat-btn-primario">
                {editId ? "Actualizar periférico" : "Añadir periférico"}
              </button>
            </div>
          </form>
        )}

        {/* Listado */}
        {loading ? (
          <div className="loader-fullscreen"><Loader /></div>
        ) : (
          <div className="overflow-tabla">
            <div className="mat-panel">
              <div className="mat-list-header mat-grid-perifericos">
                <span>Marca / Modelo</span>
                <span>Nº Serie</span>
                <span>Tipo</span>
                <span>Estado</span>
                <span>Equipo</span>
                <span>Acciones</span>
              </div>

              <div className="mat-rows">
                {pageItems.length === 0 ? (
                  <div className="mat-vacio">
                    {equipoIdParam ? "Este equipo no tiene periféricos asociados." : "No hay periféricos que coincidan con la búsqueda."}
                  </div>
                ) : (
                  pageItems.map((p) => (
                    <div key={p.id} className="mat-row mat-grid-perifericos">
                      <div className="mat-col">
                        <span className="mat-col-titulo">{p.marca} {p.modelo}</span>
                      </div>
                      <div className="mat-col">
                        <code className="mat-serie">{p.numero_serie || '—'}</code>
                      </div>
                      <div className="mat-col">{p.tipo || '—'}</div>
                      <div className="mat-col">
                        <span className={`mat-badge-estado ${claseEstado(p.estado)}`}>{p.estado}</span>
                      </div>
                      <div className="mat-col">
                        {p?.equipo
                          ? `#${p.equipo.id} · ${p.equipo.marca} ${p.equipo.modelo}`
                          : p.equipoId
                            ? `#${p.equipoId}`
                            : "—"}
                      </div>
                      <div className="mat-col mat-col-acciones">
                        <button className="mat-btn-icono" onClick={() => onEdit(p)} title="Editar">
                          <FiEdit2 />
                        </button>
                        <button className="mat-btn-icono mat-btn-icono-danger" onClick={() => pedirBorrado(p.id)} title="Eliminar">
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Paginación */}
        {filtered.length > itemsPerPage && (
          <div className="pagination">
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              Anterior
            </button>
            <span>Página {currentPage} de {totalPages}</span>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              Siguiente
            </button>
          </div>
        )}
      </div>

      <Popup
        open={!!borrarId}
        title="Eliminar periférico"
        onClose={() => setBorrarId(null)}
        showConfirm={false}
        footerActions={(
          <button className="boton-aceptar-popup" onClick={confirmarBorrado}>Eliminar</button>
        )}
      >
        <p>¿Seguro que quieres eliminar este periférico? Esta acción no se puede deshacer.</p>
      </Popup>
    </main>
  );
};

export default PerifericosManager;
